import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../../components/web/Layout';
import { getFileUrl } from '../../../utils/helper';
import { 
  CalendarOutlined, 
  ClockCircleOutlined, 
  UserOutlined,
  ArrowLeftOutlined,
  ShareAltOutlined,
  BookOutlined,
  FacebookOutlined,
  TwitterOutlined,
  LinkedinOutlined,
  LoadingOutlined,
  TagOutlined,
  EyeOutlined,
  HeartOutlined,
  CommentOutlined
} from '@ant-design/icons';
import { 
  Spin, 
  Button, 
  Tag, 
  Divider, 
  Space, 
  Typography, 
  Avatar, 
  Tooltip, 
  Dropdown,
  message,
  Image,
  Row,
  Col,
  Card,
  Statistic
} from 'antd';

const { Title, Text, Paragraph } = Typography;

function Blog() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}blog/${slug}`);
        
        if (!response.ok) {
          throw new Error('Blog not found');
        }
        
        const data = await response.json();
        setBlog(data.data);
        setLikeCount(Math.floor(Math.random() * 100) + 50); // Simulated likes
        setError(null);
      } catch (err) {
        setError(err.message);
        setBlog(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const estimateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content?.split(/\s+/).length || 0;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = blog?.title || '';

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      message.success('Link copied to clipboard!');
    } else if (platform === 'native' && navigator.share) {
      navigator.share({
        title: title,
        url: url,
      }).catch(() => {});
    } else {
      window.open(shareUrls[platform], '_blank');
    }
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
    message.success(isLiked ? 'Removed like' : 'Thanks for liking!');
  };

  const shareMenuItems = [
    {
      key: 'facebook',
      icon: <FacebookOutlined className="text-blue-600" />,
      label: 'Share on Facebook',
      onClick: () => handleShare('facebook'),
    },
    {
      key: 'twitter',
      icon: <TwitterOutlined className="text-blue-400" />,
      label: 'Share on Twitter',
      onClick: () => handleShare('twitter'),
    },
    {
      key: 'linkedin',
      icon: <LinkedinOutlined className="text-blue-700" />,
      label: 'Share on LinkedIn',
      onClick: () => handleShare('linkedin'),
    },
    {
      key: 'whatsapp',
      icon: <span className="text-green-500">📱</span>,
      label: 'Share on WhatsApp',
      onClick: () => handleShare('whatsapp'),
    },
    {
      type: 'divider',
    },
    {
      key: 'copy',
      icon: <ShareAltOutlined />,
      label: 'Copy Link',
      onClick: () => handleShare('copy'),
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Space direction="vertical" align="center" size="large">
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            <Text type="secondary" className="text-lg">Loading amazing content...</Text>
          </Space>
        </div>
      </Layout>
    );
  }

  if (error || !blog) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center px-4">
          <Card className="max-w-md text-center shadow-xl rounded-2xl">
            <div className="py-8">
              <div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-red-500 text-4xl">!</span>
              </div>
              <Title level={2} className="mb-3">{error || 'Blog not found'}</Title>
              <Paragraph type="secondary" className="mb-6">
                The blog post you're looking for doesn't exist or may have been moved.
              </Paragraph>
              <Link to="/blogs">
                <Button type="primary" size="large" icon={<ArrowLeftOutlined />}>
                  Back to Blogs
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <article className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative h-[70vh] overflow-hidden">
          <Image
            src={getFileUrl(blog.thumbnail, 'blog')}
            alt={blog.title}
            className="w-full h-full object-cover"
            preview={false}
            style={{
              filter: 'brightness(0.7)',
              transform: 'scale(1.02)',
              transition: 'transform 0.3s ease'
            }}
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          
          {/* Content Overlay */}
          <div className="absolute bottom-0 left-0 right-0 z-10 p-8 md:p-16 text-white">
            <div className="max-w-4xl mx-auto">
              <Space size={[8, 16]} wrap className="mb-4">
                {blog.category && (
                  <Tag color="blue" className="px-4 py-1 text-sm rounded-full">
                    {blog.category}
                  </Tag>
                )}
                <Space size="middle" className="text-gray-300">
                  <span>
                    <CalendarOutlined className="mr-2" />
                    {formatDate(blog.created_at)}
                  </span>
                  <span>
                    <ClockCircleOutlined className="mr-2" />
                    {estimateReadTime(blog.description)} min read
                  </span>
                  <span>
                    <EyeOutlined className="mr-2" />
                    {Math.floor(Math.random() * 1000) + 100} views
                  </span>
                </Space>
              </Space>
              
              <Title level={1} className="text-white !mb-6 !text-4xl md:!text-5xl">
                {blog.title}
              </Title>

              {blog.author && (
                <Space size="middle" align="center">
                  <Avatar 
                    size={48} 
                    icon={<UserOutlined />}
                    className="bg-gradient-to-r from-blue-500 to-purple-500"
                  >
                    {blog.author.charAt(0)}
                  </Avatar>
                  <div>
                    <Text strong className="text-white block text-lg">{blog.author}</Text>
                    <Text type="secondary" className="text-gray-300">Author • Content Creator</Text>
                  </div>
                </Space>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <Row gutter={[32, 32]}>
            {/* Left Column - Main Content */}
            <Col xs={24} lg={16}>
              {/* Article Content */}
              <Card className="shadow-lg rounded-2xl mb-8" bordered={false}>
                <div 
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: blog.description }}
                />
              </Card>

              {/* Tags Section */}
              {blog.tags && blog.tags.length > 0 && (
                <Card className="shadow-lg rounded-2xl mb-8" bordered={false}>
                  <Space direction="vertical" size="middle" className="w-full">
                    <Space align="center">
                      <TagOutlined className="text-xl" />
                      <Title level={4} className="!mb-0">Related Tags</Title>
                    </Space>
                    <Space size={[8, 16]} wrap>
                      {blog.tags.map((tag, index) => (
                        <Tag 
                          key={index}
                          className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors"
                          color="blue"
                        >
                          #{tag}
                        </Tag>
                      ))}
                    </Space>
                  </Space>
                </Card>
              )}

              {/* Gallery Section */}
              {blog.images && blog.images.length > 0 && (
                <Card 
                  title={
                    <Space>
                      <span className="text-2xl">📸</span>
                      <Title level={4} className="!mb-0">Blog Gallery</Title>
                    </Space>
                  }
                  className="shadow-lg rounded-2xl"
                  bordered={false}
                >
                  <Row gutter={[16, 16]}>
                    {blog.images.map((img, index) => (
                      <Col xs={12} sm={8} md={6} key={img.id || index}>
                        <Image
                          src={img.image}
                          alt={`Gallery ${index + 1}`}
                          className="rounded-xl cursor-pointer"
                          preview={{
                            mask: <span className="text-white">Preview</span>,
                          }}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: '12px',
                            transition: 'transform 0.3s ease'
                          }}
                        />
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}
            </Col>

            {/* Right Column - Sidebar */}
            <Col xs={24} lg={8}>
              <div className="sticky top-24 space-y-6">
                {/* Action Buttons */}
                <Card className="shadow-lg rounded-2xl" bordered={false}>
                  <Space direction="vertical" size="middle" className="w-full">
                    <Dropdown menu={{ items: shareMenuItems }} placement="bottomRight" trigger={['click']}>
                      <Button 
                        type="primary" 
                        size="large" 
                        block
                        icon={<ShareAltOutlined />}
                        className="h-12 text-base bg-gradient-to-r from-blue-600 to-purple-600 border-0"
                      >
                        Share Article
                      </Button>
                    </Dropdown>

                    <Space.Compact block>
                      <Button 
                        size="large" 
                        block
                        icon={<HeartOutlined />}
                        onClick={handleLike}
                        type={isLiked ? 'primary' : 'default'}
                        danger={isLiked}
                      >
                        {isLiked ? 'Liked' : 'Like'} ({likeCount})
                      </Button>
                      <Button 
                        size="large"
                        icon={<BookOutlined />}
                        onClick={() => message.success('Saved to bookmarks!')}
                      >
                        Save
                      </Button>
                    </Space.Compact>
                  </Space>
                </Card>

                {/* Article Stats */}
                <Card className="shadow-lg rounded-2xl" bordered={false}>
                  <Title level={4}>Article Statistics</Title>
                  <Divider className="my-4" />
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Statistic 
                        title="Published" 
                        value={formatDate(blog.created_at)}
                        valueStyle={{ fontSize: '14px' }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic 
                        title="Reading Time" 
                        value={`${estimateReadTime(blog.description)} min`}
                      />
                    </Col>
                  
                  </Row>
                </Card>

                {blog.author && (
                  <Card className="shadow-lg rounded-2xl" bordered={false}>
                    <Space direction="vertical" size="middle" className="w-full">
                      <Space size="large" align="center">
                        <Avatar 
                          size={64} 
                          className="bg-gradient-to-r from-blue-500 to-purple-500"
                          icon={<UserOutlined />}
                        >
                          {blog.author.charAt(0)}
                        </Avatar>
                        <div>
                          <Title level={4} className="!mb-1">{blog.author}</Title>
                          <Text type="secondary">Content Creator</Text>
                        </div>
                      </Space>
                      <Paragraph className="text-gray-600">
                        Expert writer specializing in technology and innovation. 
                        Passionate about sharing knowledge and insights with the community.
                      </Paragraph>
                      <Button block>View Profile</Button>
                    </Space>
                  </Card>
                )}

                {/* Related Posts Suggestion */}
                <Card className="shadow-lg rounded-2xl bg-gradient-to-r from-blue-50 to-purple-50" bordered={false}>
                  <Space direction="vertical" size="middle" className="w-full text-center">
                    <Title level={4} className="!mb-0">Enjoyed this article?</Title>
                    <Paragraph className="text-gray-600">
                      Check out more articles from our blog
                    </Paragraph>
                    <Link to="/blogs">
                      <Button type="primary" ghost size="large">
                        Browse All Articles
                      </Button>
                    </Link>
                  </Space>
                </Card>
              </div>
            </Col>
          </Row>
        </div>

        {/* Mobile Action Bar */}
        <div className="lg:hidden fixed bottom-6 right-6 z-50">
          <Space>
            <Tooltip title="Save">
              <Button 
                shape="circle" 
                size="large"
                icon={<BookOutlined />}
                className="shadow-lg bg-white"
                onClick={() => message.success('Saved to bookmarks!')}
              />
            </Tooltip>
            <Tooltip title="Share">
              <Button 
                type="primary" 
                shape="circle" 
                size="large"
                icon={<ShareAltOutlined />}
                className="shadow-lg"
                onClick={() => handleShare('native')}
              />
            </Tooltip>
          </Space>
        </div>
      </article>
    </Layout>
  );
}

export default Blog;